// src/components/layout/DancersImportModal.js - bulk import of dancers from Excel.
//
// Audit fixes (FL-17, FL-02, FL-14, FL-29):
// * the backend's Czech message and the list of faulty rows ("details") are shown;
// * choosing the same (corrected) file again works - the input is reset;
// * the preview marks dancers who are already in the group (they are skipped);
// * busy states while reading and importing; the preview table scrolls sideways on phones;
// * the shared Modal (Esc, focus, sheet on phones).
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileSpreadsheet, Upload } from 'lucide-react';
import config from '../../config';
import { dancersApi, toApiError } from '../../services/api';
import { formatDate, countLabel } from '../../utils/format';
import { Alert, Button, Modal } from '../ui';

const DANCER_FORMS = ['tanečník', 'tanečníci', 'tanečníků'];
// "Importovat 1 tanečníka / 3 tanečníky / 5 tanečníků"
const DANCER_ACCUSATIVE_FORMS = ['tanečníka', 'tanečníky', 'tanečníků'];
const MAX_IMPORT_MB = 10;

const normalise = (value) => String(value || '').trim().toLowerCase();
const dancerKey = (d) => `${normalise(d.firstName)}|${normalise(d.lastName)}|${String(d.birthDate || '').slice(0, 10)}`;

const DancersImportModal = ({ onClose, onConfirm, existingDancers = [] }) => {
  const inputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null); // { message, details }
  const [phase, setPhase] = useState('idle'); // idle | reading | importing
  const busy = phase !== 'idle';

  // Stable close handler for the Modal (it re-focuses whenever onClose changes).
  const busyRef = useRef(false);
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    busyRef.current = busy;
    onCloseRef.current = onClose;
  });
  const handleClose = useCallback(() => {
    if (!busyRef.current && onCloseRef.current) onCloseRef.current();
  }, []);

  const existingKeys = useMemo(() => new Set(existingDancers.map(dancerKey)), [existingDancers]);

  const rows = useMemo(() => {
    const seen = new Set();
    return ((preview && preview.dancers) || []).map((d) => {
      const key = dancerKey(d);
      const duplicate = existingKeys.has(key);
      const repeated = !duplicate && seen.has(key);
      seen.add(key);
      return { ...d, duplicate, repeated };
    });
  }, [preview, existingKeys]);
  const newCount = rows.filter((r) => !r.duplicate && !r.repeated).length;
  const skippedCount = rows.length - newCount;

  const showError = (err) => {
    const apiError = toApiError(err);
    setError({ message: apiError.message, details: Array.isArray(apiError.details) ? apiError.details : null });
  };

  const handleFileSelect = async (e) => {
    const selected = e.target.files && e.target.files[0];
    // Reset, so that choosing the same file again (after fixing it) fires onChange.
    e.target.value = '';
    if (!selected) return;

    setError(null);
    setPreview(null);
    setFile(null);
    const extension = (selected.name.split('.').pop() || '').toLowerCase();
    if (!['xlsx', 'xlsm'].includes(extension)) {
      setError({
        message:
          extension === 'xls'
            ? 'Starý formát .xls není podporován. V Excelu soubor uložte jako „Sešit aplikace Excel (.xlsx)“.'
            : 'Nahrajte prosím soubor Excel (.xlsx).',
      });
      return;
    }
    if (selected.size > MAX_IMPORT_MB * 1024 * 1024) {
      setError({ message: `Soubor je příliš velký (nejvýše ${MAX_IMPORT_MB} MB).` });
      return;
    }

    setPhase('reading');
    try {
      const response = await dancersApi.preview(selected);
      setPreview(response.data);
      setFile(selected);
    } catch (err) {
      showError(err);
    } finally {
      setPhase('idle');
    }
  };

  const handleConfirm = async () => {
    if (!file || !preview || busy) return;
    setPhase('importing');
    setError(null);
    try {
      const response = await dancersApi.bulkCreate(file);
      setPhase('idle');
      if (onConfirm) onConfirm(response.data);
    } catch (err) {
      showError(err);
      setPhase('idle');
    }
  };

  const openPicker = () => {
    if (!busy && inputRef.current) inputRef.current.click();
  };

  return (
    <>
      {/* Rendered outside the dialog portal; the buttons inside open it. */}
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept=".xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        onChange={handleFileSelect}
        disabled={busy}
        aria-label="Vybrat soubor Excel s tanečníky"
      />
      <Modal
        title="Import tanečníků z Excelu"
        onClose={handleClose}
        size="xl"
        closeOnBackdrop={false}
        footer={
          <>
            <Button variant="secondary" onClick={handleClose} disabled={busy}>
              Zrušit
            </Button>
            {preview && (
              <Button onClick={handleConfirm} loading={phase === 'importing'} disabled={newCount === 0}>
                {phase === 'importing' ? 'Importuji…' : `Importovat ${countLabel(newCount, DANCER_ACCUSATIVE_FORMS)}`}
              </Button>
            )}
          </>
        }
      >
        <div className="space-y-5">
          <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700">
            <li>Stáhněte si vzorovou tabulku.</li>
            <li>
              Vyplňte sloupce Jméno, Příjmení a Datum narození (např. 29. 9. 1995 nebo 1995-09-29). Každý tanečník na jeden
              řádek.
            </li>
            <li>Soubor uložte jako .xlsx a nahrajte ho sem. Před importem uvidíte náhled.</li>
          </ol>

          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={`${config.API_BASE_URL}/dancers/sample-xlsx`}
              download
              className="inline-flex items-center justify-center gap-2 rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <Download className="h-4 w-4" aria-hidden="true" />
              Stáhnout vzorovou tabulku
            </a>
            <Button onClick={openPicker} loading={phase === 'reading'} disabled={phase === 'importing'}>
              <Upload className="h-4 w-4" aria-hidden="true" />
              {phase === 'reading' ? 'Načítám soubor…' : preview ? 'Nahrát jiný soubor' : 'Nahrát soubor Excel'}
            </Button>
          </div>

          {error && (
            <Alert tone="error" title={error.message} details={error.details}>
              {error.details && error.details.length > 0 && 'Opravte prosím tyto řádky v Excelu a nahrajte soubor znovu:'}
            </Alert>
          )}

          {preview && (
            <div className="space-y-3">
              <p className="flex items-start gap-2 text-sm text-gray-700">
                <FileSpreadsheet className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-700" aria-hidden="true" />
                <span>
                  {file ? `${file.name}: ` : ''}V souboru je {countLabel(rows.length, DANCER_FORMS)}.
                  {skippedCount > 0 &&
                    ` ${countLabel(skippedCount, DANCER_FORMS)} už v seznamu máte nebo se v souboru opakuje – ${
                      skippedCount === 1 ? 'bude přeskočen' : 'budou přeskočeni'
                    }.`}
                </span>
              </p>
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200 text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                        Jméno
                      </th>
                      <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                        Příjmení
                      </th>
                      <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                        Datum narození
                      </th>
                      <th scope="col" className="px-4 py-2 text-left font-medium text-gray-700">
                        Stav
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {rows.map((dancer, index) => (
                      <tr key={index} className={dancer.duplicate || dancer.repeated ? 'bg-gray-50 text-gray-500' : ''}>
                        <td className="whitespace-nowrap px-4 py-2">{dancer.firstName}</td>
                        <td className="whitespace-nowrap px-4 py-2">{dancer.lastName}</td>
                        <td className="whitespace-nowrap px-4 py-2">{formatDate(dancer.birthDate)}</td>
                        <td className="whitespace-nowrap px-4 py-2">
                          {dancer.duplicate
                            ? 'Už existuje – přeskočí se'
                            : dancer.repeated
                            ? 'V souboru dvakrát – přeskočí se'
                            : 'Nový'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};

export default DancersImportModal;
